import * as T from 'three';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';

// Static world cells share vertices/materials in one draw, retaining their
// individual PVS and frustum tests. Unlike BatchedMesh this needs no transform
// textures or shader variants. Only a changed cell selection uploads indices.
export class WorldBatches {
    constructor(items) {
        this.batches = [];
        this.hooks = [];
        const groups = new Map();
        for (const item of items) {
            const mesh = item.mesh, material = mesh.material, geometry = mesh.geometry;
            if (item.model !== 0 || item.prop || !mesh.parent?.isScene || Array.isArray(material) || material.transparent || material.userData.water || !material.depthWrite || geometry.groups.length || !geometry.index) continue;
            const layout = Object.entries(geometry.attributes).map(([key, a]) => `${key}:${a.itemSize}:${a.normalized}:${a.array.constructor.name}`).sort().join(',');
            const key = `${mesh.parent.id}:${material.id}:${mesh.castShadow}:${mesh.receiveShadow}:${layout}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(mesh);
        }
        for (const members of groups.values()) {
            if (members.length < 2) continue;
            const parent = members[0].parent, material = members[0].material;
            const sources = members.map(mesh => mesh.geometry.clone().applyMatrix4(mesh.matrix));
            const geometry = mergeGeometries(sources), allIndices = geometry.index.array.slice();
            const cells = []; let offset = 0;
            for (let id = 0; id < members.length; id++) {
                const source = sources[id]; source.computeBoundingSphere();
                cells.push({mesh:members[id], indices:allIndices.subarray(offset, offset+source.index.count), bounds:source.boundingSphere.clone(), inFrustum:true, visible:undefined});
                offset += source.index.count; source.dispose(); members[id].removeFromParent();
                // Loading previews may have uploaded these cells already.
                // Retain their CPU data for metadata/debug/restore, release VBOs.
                members[id].geometry.dispose();
            }
            geometry.index.setUsage(T.DynamicDrawUsage);
            const batch = new T.Mesh(geometry, material);
            batch.name = 'Static world cells'; batch.frustumCulled = false; batch.layers.enable(1);
            batch.castShadow = members[0].castShadow; batch.receiveShadow = members[0].receiveShadow;
            batch.matrixAutoUpdate = false;
            const clip = new T.Matrix4(), lastClip = new T.Matrix4(), frustum = new T.Frustum(); let prepared = false;
            const prepare = (camera, shadow = false) => {
                clip.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(batch.matrixWorld);
                const moved = !prepared || !lastClip.equals(clip);
                if (moved) {frustum.setFromProjectionMatrix(clip, camera.coordinateSystem, camera.reversedDepth);lastClip.copy(clip);}
                let changed = !prepared;
                for (const cell of cells) {
                    if (moved) cell.inFrustum = !cell.mesh.frustumCulled || frustum.intersectsSphere(cell.bounds);
                    const visible = cell.inFrustum && cell.mesh.visible && (shadow ? cell.mesh.castShadow : cell.mesh.layers.test(camera.layers));
                    if (visible !== cell.visible) {cell.visible=visible;changed=true;}
                }
                prepared = true;
                if (!changed) return;
                let count=0;
                for (const cell of cells) if (cell.visible) {geometry.index.array.set(cell.indices,count);count+=cell.indices.length;}
                geometry.setDrawRange(0,count);
                if (count) {geometry.index.clearUpdateRanges();geometry.index.addUpdateRange(0,count);geometry.index.needsUpdate=true;}
            };
            // The scene hook already prepares the color selection before Three
            // builds its render list. Only a shadow pass can replace it before
            // the draw; avoid scanning every cell a second time in other passes.
            let shadowSelection = false;
            batch.onBeforeRender = (renderer, scene, camera) => {
                if (shadowSelection) { prepare(camera); shadowSelection = false; }
            };
            batch.onBeforeShadow = (renderer, object, camera, shadowCamera) => {
                prepare(shadowCamera, true); shadowSelection = true;
            };
            parent.add(batch);this.batches.push({batch,members,parent,cells,prepare});
        }
        for (const parent of new Set(this.batches.map(entry => entry.parent))) {
            const entries = this.batches.filter(entry => entry.parent === parent), previous = parent.onBeforeRender;
            const hook = function(renderer, scene, camera, target) {
                previous.call(this, renderer, scene, camera, target);
                // Prepare before Three builds its render list. Empty batches
                // stay available to shadow cameras on the off-PVS caster layer.
                for (const {batch,prepare} of entries) {prepare(camera);batch.layers.set(batch.geometry.drawRange.count ? 0 : 1);}
            };
            parent.onBeforeRender = hook; this.hooks.push({parent,previous,hook});
        }
    }
    dispose() {
        for (const {parent,previous,hook} of this.hooks) if (parent.onBeforeRender === hook) parent.onBeforeRender=previous;
        this.hooks.length=0;
        for (const {batch,members,parent} of this.batches) {batch.removeFromParent();batch.geometry.dispose();for(const mesh of members)parent.add(mesh);}
        this.batches.length=0;
    }
}
